'use client';

import React, { useId, useState } from 'react';
import { CalendarDate } from '@internationalized/date';
import { composeTailwindRenderProps } from '@utils';
import {
	DateField as DateFieldPrimitive,
	DateInput as DateInputPrimitive,
	DateSegment,
	I18nProvider,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type {
	DateFieldProps as DateFieldPrimitiveProps,
	DateInputProps,
	DateValue,
	ValidationResult,
} from 'react-aria-components';

interface DateFieldProps<T extends DateValue> extends DateFieldPrimitiveProps<T> {
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	theme?: 'light' | 'dark';
	locale?: string;
}

const segmentStyles = tv({
	base: 'inline shrink-0 rounded-xs px-0.5 text-sm tabular-nums outline-0',
	variants: {
		theme: {
			light: 'text-light-text focus:bg-light-focus/20',
			dark: 'text-dark-text focus:bg-dark-focus/40',
		},
		isPlaceholder: {
			true: '',
		},
	},
	compoundVariants: [
		{
			isPlaceholder: true,
			theme: 'light',
			className: 'text-light-text-lighter',
		},
		{
			isPlaceholder: true,
			theme: 'dark',
			className: 'text-dark-text-lighter',
		},
	],
	defaultVariants: {
		theme: 'light',
	},
});

const DateInput = ({
	className,
	theme,
	...props
}: Omit<DateInputProps, 'children'> & { theme?: 'light' | 'dark' }) => {
	return (
		<DateFieldPrimitive placeholderValue={new CalendarDate(2000, 1, 1)}>
			<DateInputPrimitive
				className={composeTailwindRenderProps(className, 'bg-transparent text-sm')}
				{...props}
			>
				{segment => (
					<DateSegment
						segment={segment}
						className={segmentStyles({ theme, isPlaceholder: segment.isPlaceholder })}
					>
						{segment.text}
					</DateSegment>
				)}
			</DateInputPrimitive>
		</DateFieldPrimitive>
	);
};

export type { DateFieldProps };
export { DateInput, segmentStyles };
